/**
 * Canvas effects used by the opening animation.
 *
 * This is a TypeScript port of the particle section in the supplied external
 * HTML (`#birth-fx`).  Keeping the renderer separate from React lets the same
 * effect timing run in a normal browser and inside the iOS WKWebView.
 */

type ParticleType = 'dot' | 'spark' | 'ring' | 'missile'

type Particle = {
  type: ParticleType
  x: number
  y: number
  vx: number
  vy: number
  g: number
  drag: number
  c: string
  s: number
  life: number
  decay: number
  rot: number
  vr: number
  r?: number
  x0?: number
  y0?: number
  x1?: number
  y1?: number
  t?: number
  speed?: number
  arc?: number
  ang?: number
  onHit?: () => void
}

type BurstOptions = {
  n?: number
  sp?: number
  c?: string[]
  g?: number
  s?: number
  decay?: number
  dirx?: number
  diry?: number
  up?: number
  drag?: number
  type?: 'dot' | 'spark'
}

type BirthFxCallbacks = {
  flash: (gold: boolean) => void
  shake: () => void
  schedule: (callback: () => void, delay: number) => void
}

const TAU = Math.PI * 2

export class BirthFxRenderer {
  private particles: Particle[] = []
  private running = false

  constructor(private readonly callbacks: BirthFxCallbacks) {}

  reset() {
    this.particles = []
    this.running = false
  }

  start() {
    this.running = true
  }

  stop() {
    this.running = false
    this.particles = []
  }

  /** Draw one frame. The caller clears the canvas before calling this method. */
  render(ctx: CanvasRenderingContext2D) {
    if (!this.running) return

    for (let index = this.particles.length - 1; index >= 0; index -= 1) {
      const particle = this.particles[index]
      if (particle.type === 'missile') {
        particle.t = (particle.t ?? 0) + (particle.speed ?? 0.03)
        if ((particle.t ?? 0) >= 1) {
          this.particles.splice(index, 1)
          particle.onHit?.()
          continue
        }

        const t = particle.t ?? 0
        const x0 = particle.x0 ?? particle.x
        const y0 = particle.y0 ?? particle.y
        const x1 = particle.x1 ?? particle.x
        const y1 = particle.y1 ?? particle.y
        const arc = particle.arc ?? 0
        particle.x = x0 + (x1 - x0) * t
        particle.y = y0 + (y1 - y0) * t - Math.sin(t * Math.PI) * arc
        const tangentX = x1 - x0
        const tangentY = (y1 - y0) - Math.cos(t * Math.PI) * Math.PI * arc
        particle.ang = Math.atan2(tangentY, tangentX)

        // White smoke tail.
        if (this.particles.length < 620) {
          this.particles.push({
            x: particle.x - Math.cos(particle.ang) * 12,
            y: particle.y - Math.sin(particle.ang) * 12,
            vx: (Math.random() - 0.5) * 0.7,
            vy: 0.1 + Math.random() * 0.6,
            g: 0,
            drag: 0.9,
            c: `rgba(235,235,242,${0.3 + Math.random() * 0.3})`,
            s: 8 + Math.random() * 9,
            life: 1,
            decay: 0.042,
            rot: 0,
            vr: 0,
            type: 'dot'
          })
        }
        // Hot sparks in the exhaust.
        if (this.particles.length < 680) {
          this.particles.push({
            x: particle.x - Math.cos(particle.ang) * 22,
            y: particle.y - Math.sin(particle.ang) * 22,
            vx: (Math.random() - 0.5) * 2,
            vy: 1 + Math.random() * 3.5,
            g: 0,
            drag: 0.86,
            c: ['#ffb347', '#ff7a3c', '#ffd54a', '#fff2b0'][Math.floor(Math.random() * 4)],
            s: 3.5 + Math.random() * 3.5,
            life: 1,
            decay: 0.075,
            rot: 0,
            vr: 0,
            type: 'dot'
          })
        }
        this.drawMissile(ctx, particle)
        continue
      }

      particle.x += particle.vx
      particle.y += particle.vy
      particle.vy += particle.g
      particle.vx *= particle.drag
      particle.vy *= particle.drag
      particle.life -= particle.decay
      particle.rot += particle.vr
      if (particle.life <= 0) {
        this.particles.splice(index, 1)
        continue
      }

      ctx.save()
      ctx.translate(particle.x, particle.y)
      ctx.globalAlpha = Math.max(0, particle.life)
      if (particle.type === 'spark') {
        ctx.rotate(particle.rot)
        ctx.fillStyle = particle.c
        ctx.fillRect(-particle.s / 2, -particle.s / 3, particle.s, particle.s * 0.66)
      } else if (particle.type === 'ring') {
        ctx.strokeStyle = particle.c
        ctx.lineWidth = particle.s * particle.life
        ctx.beginPath()
        ctx.arc(0, 0, (particle.r ?? 60) * (1.6 - particle.life), 0, TAU)
        ctx.stroke()
      } else {
        ctx.fillStyle = particle.c
        ctx.beginPath()
        ctx.arc(0, 0, particle.s * particle.life, 0, TAU)
        ctx.fill()
      }
      ctx.restore()
    }
  }

  heartBurst(x: number, y: number) {
    this.start()
    this.burst(x, y, { n: 90, sp: 14, c: ['#e55454', '#ff6b6b', '#ffd0d0', '#a02020'], g: 0.22, s: 5, decay: 0.02 })
    this.burst(x, y, { n: 40, sp: 9, c: ['#ffffff', '#ffd0d0'], g: 0.1, s: 3, decay: 0.03, type: 'dot' })
    this.callbacks.shake()
    this.callbacks.flash(false)
  }

  babyBurst(x: number, y: number) {
    this.start()
    this.burst(x, y - 10, { n: 70, sp: 10, c: ['#ffd54a', '#fff2b0', '#ffb300', '#ffffff'], g: 0.06, s: 4, decay: 0.024 })
    this.burst(x, y, { n: 26, sp: 20, c: ['#ffffff', '#fff2b0'], g: 0.05, s: 2.5, decay: 0.026, type: 'dot' })
    this.callbacks.flash(true)
  }

  frameImpact(x: number, y: number) {
    this.start()
    this.ring(x, y + 60, '#ffffff')
    this.ring(x, y + 60, '#ffd54a')
    this.burst(x, y + 90, { n: 40, sp: 8, c: ['#c9b8a0', '#a89070', '#e8dcc8'], g: 0.35, s: 4, decay: 0.02, diry: -1, up: 6 })
    this.callbacks.shake()
    this.callbacks.flash(false)
  }

  launchMissile(x0: number, y0: number, x1: number, y1: number, speed: number, onHit: () => void) {
    this.start()
    this.particles.push({
      type: 'missile',
      x: x0,
      y: y0,
      x0,
      y0,
      x1,
      y1,
      t: 0,
      speed,
      arc: 0,
      vx: 0,
      vy: 0,
      g: 0,
      drag: 1,
      c: '#fff',
      s: 1,
      life: 1,
      decay: 0,
      rot: 0,
      vr: 0,
      ang: Math.atan2(y1 - y0, x1 - x0),
      onHit
    })
  }

  private drawMissile(ctx: CanvasRenderingContext2D, particle: Particle) {
    const angle = particle.ang ?? 0
    ctx.save()
    ctx.translate(particle.x, particle.y)
    ctx.rotate(angle)

    const flame = 20 + Math.sin((particle.t ?? 0) * 50) * 7
    ctx.fillStyle = 'rgba(255,110,35,.5)'
    ctx.beginPath(); ctx.moveTo(-26, 0); ctx.lineTo(-26 - flame - 10, 10); ctx.lineTo(-26 - flame - 10, -10); ctx.closePath(); ctx.fill()
    ctx.fillStyle = 'rgba(255,185,75,.9)'
    ctx.beginPath(); ctx.moveTo(-26, 0); ctx.lineTo(-26 - flame, 5.5); ctx.lineTo(-26 - flame, -5.5); ctx.closePath(); ctx.fill()
    ctx.fillStyle = '#fff8e0'
    ctx.beginPath(); ctx.moveTo(-24, 0); ctx.lineTo(-24 - flame * 0.55, 2.4); ctx.lineTo(-24 - flame * 0.55, -2.4); ctx.closePath(); ctx.fill()

    ctx.fillStyle = '#45542a'
    ctx.beginPath(); ctx.moveTo(-15, -3.5); ctx.lineTo(-23, -10.5); ctx.lineTo(-9, -8.5); ctx.closePath(); ctx.fill()
    ctx.beginPath(); ctx.moveTo(-15, 3.5); ctx.lineTo(-23, 10.5); ctx.lineTo(-9, 8.5); ctx.closePath(); ctx.fill()

    ctx.fillStyle = '#6a7a42'; ctx.fillRect(-23, -4.4, 37, 8.8)
    ctx.fillStyle = 'rgba(255,255,255,.16)'; ctx.fillRect(-23, -3.6, 37, 1.5)
    ctx.fillStyle = '#c83430'; ctx.fillRect(-9, -4.6, 5, 9.2)
    ctx.fillStyle = '#e8e8e8'; ctx.fillRect(-2, -3, 8, 6)
    ctx.fillStyle = '#dfe3e8'
    ctx.beginPath(); ctx.moveTo(28, 0); ctx.lineTo(14, 4.6); ctx.lineTo(14, -4.6); ctx.closePath(); ctx.fill()
    ctx.fillStyle = 'rgba(255,255,255,.4)'
    ctx.beginPath(); ctx.moveTo(28, 0); ctx.lineTo(14, -4.6); ctx.lineTo(14, 0); ctx.closePath(); ctx.fill()
    ctx.restore()
  }

  private dfBoom(x: number, y: number) {
    this.start()
    this.burst(x, y, { n: 80, sp: 20, c: ['#ffffff', '#fff8e0', '#ffe08a'], g: 0.05, s: 4.5, decay: 0.032, type: 'dot' })
    this.burst(x, y, { n: 116, sp: 15, c: ['#ff5a3c', '#ff9a3c', '#ffd54a', '#ff3d2e', '#fff3b0'], g: 0.09, s: 7.5, decay: 0.02 })
    this.burst(x, y, { n: 48, sp: 7, c: ['#ffb347', '#ff7a3c', '#ffd54a'], g: -0.03, s: 4.5, decay: 0.024, up: 5 })
    this.burst(x, y - 34, { n: 68, sp: 11, c: ['#7a6a52', '#5d4f3c', '#8a7a60', '#4a3e2e'], g: -0.012, s: 10.5, decay: 0.016, up: 5, type: 'dot' })
    this.burst(x, y - 10, { n: 42, sp: 3.5, c: ['#6b5d48', '#8a7a60', '#54463a'], g: -0.09, s: 8.5, decay: 0.016, up: 15, type: 'dot' })
    this.burst(x - 18, y + 4, { n: 24, sp: 9, c: ['#ff6a3c', '#ffd54a', '#fff2b0'], g: 0.12, s: 3.5, decay: 0.026, type: 'spark' })
    this.ring(x, y, '#ffffff')
    this.callbacks.schedule(() => this.ring(x, y, '#ffd54a'), 70)
    this.callbacks.schedule(() => this.ring(x, y, '#ff6a3c'), 150)
    this.callbacks.schedule(() => this.ring(x, y, '#fff2b0'), 250)
    this.callbacks.shake()
    this.callbacks.schedule(() => this.callbacks.shake(), 200)
    this.callbacks.schedule(() => this.callbacks.shake(), 360)
    this.callbacks.flash(false)
    this.callbacks.schedule(() => this.callbacks.flash(true), 160)
  }

  missileImpact(x: number, y: number) {
    this.dfBoom(x, y)
  }

  private burst(x: number, y: number, options: BurstOptions) {
    this.start()
    const n = options.n ?? 30
    const speed = options.sp ?? 6
    const colors = options.c ?? ['#fff']
    const gravity = options.g ?? 0.15
    const size = options.s ?? 4
    const type = options.type ?? 'spark'
    for (let index = 0; index < n; index += 1) {
      const angle = Math.random() * TAU
      const velocity = speed * (0.35 + Math.random() * 0.85)
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * velocity * (options.dirx ?? 1),
        vy: Math.sin(angle) * velocity * (options.diry ?? 1) - (options.up ?? 0),
        g: gravity,
        drag: options.drag ?? 0.985,
        c: colors[Math.floor(Math.random() * colors.length)],
        s: size * (0.6 + Math.random() * 0.9),
        life: 1,
        decay: options.decay ?? 0.028,
        rot: Math.random() * TAU,
        vr: (Math.random() - 0.5) * 0.3,
        type
      })
    }
  }

  private ring(x: number, y: number, color: string) {
    this.start()
    this.particles.push({
      x,
      y,
      vx: 0,
      vy: 0,
      g: 0,
      drag: 1,
      c: color,
      s: 6,
      life: 1,
      decay: 0.035,
      rot: 0,
      vr: 0,
      type: 'ring',
      r: 60
    })
  }

  createMissileImpact(x: number, y: number) {
    return () => this.missileImpact(x, y)
  }
}
